// src/pages/UniformSchedule.js

import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Clock,
  Sparkles,
  Download,
  Eye,
  CheckCircle2,
  AlertCircle,
  Shirt,
  Footprints,
  IdCard,
  UserCheck,
  ShieldCheck,
  ArrowRight,
  X,
  Phone,
  Printer
} from 'lucide-react';
import StructuredData from '../components/StructuredData';
import '../styles/UniformSchedule.scss';

const DAYS_SCHEDULE = [
  {
    key: 'monday',
    dayName: 'Monday',
    dayHindi: 'सोमवार',
    dayIndex: 1,
    type: 'REGULAR SCHOOL UNIFORM',
    typeHindi: 'नियमित स्कूल यूनिफॉर्म',
    isHouse: false,
    colorTheme: '#011673',
    bgBadge: 'badge-navy',
    description: 'Sky Blue Shirt, Black Pant/Skirt',
    descriptionHindi: 'आसमानी नीली शर्ट, काली पैंट/स्कर्ट',
    items: [
      'Sky Blue collared shirt (neatly ironed)',
      'Black formal trousers (Boys) / Pleated skirt (Girls)',
      'School tie & belt',
      'Black school shoes & matching socks',
      'School ID card mandatory'
    ],
    tag: 'Start of Week'
  },
  {
    key: 'tuesday',
    dayName: 'Tuesday',
    dayHindi: 'मंगलवार',
    dayIndex: 2,
    type: 'REGULAR SCHOOL UNIFORM',
    typeHindi: 'नियमित स्कूल यूनिफॉर्म',
    isHouse: false,
    colorTheme: '#1b5e20',
    bgBadge: 'badge-green',
    description: 'Sky Blue Shirt, Black Pant/Skirt',
    descriptionHindi: 'आसमानी नीली शर्ट, काली पैंट/स्कर्ट',
    items: [
      'Sky Blue collared shirt (neatly ironed)',
      'Black formal trousers (Boys) / Pleated skirt (Girls)',
      'School tie & belt',
      'Black school shoes & matching socks',
      'School ID card mandatory'
    ],
    tag: 'Academic Day'
  },
  {
    key: 'wednesday',
    dayName: 'Wednesday',
    dayHindi: 'बुधवार',
    dayIndex: 3,
    type: 'HOUSE DRESS',
    typeHindi: 'हाउस ड्रेस',
    isHouse: true,
    colorTheme: '#5e35b1',
    bgBadge: 'badge-purple',
    description: 'House T-Shirt & Track Pants',
    descriptionHindi: 'हाउस टी-शर्ट और ट्रैक पैंट',
    items: [
      'Assigned House Color T-Shirt (Red / Blue / Green / Yellow)',
      'Official school track pants with house stripes',
      'Sports / PT shoes with white socks',
      'Comfortable fit for physical exercises & sports',
      'School ID card mandatory'
    ],
    tag: 'Sports & House Activity'
  },
  {
    key: 'thursday',
    dayName: 'Thursday',
    dayHindi: 'गुरुवार',
    dayIndex: 4,
    type: 'REGULAR SCHOOL UNIFORM',
    typeHindi: 'नियमित स्कूल यूनिफॉर्म',
    isHouse: false,
    colorTheme: '#e65100',
    bgBadge: 'badge-orange',
    description: 'Sky Blue Shirt, Black Pant/Skirt',
    descriptionHindi: 'आसमानी नीली शर्ट, काली पैंट/स्कर्ट',
    items: [
      'Sky Blue collared shirt (neatly ironed)',
      'Black formal trousers (Boys) / Pleated skirt (Girls)',
      'School tie & belt',
      'Black school shoes & matching socks',
      'School ID card mandatory'
    ],
    tag: 'Academic Day'
  },
  {
    key: 'friday',
    dayName: 'Friday',
    dayHindi: 'शुक्रवार',
    dayIndex: 5,
    type: 'REGULAR SCHOOL UNIFORM',
    typeHindi: 'नियमित स्कूल यूनिफॉर्म',
    isHouse: false,
    colorTheme: '#c2185b',
    bgBadge: 'badge-pink',
    description: 'Sky Blue Shirt, Black Pant/Skirt',
    descriptionHindi: 'आसमानी नीली शर्ट, काली पैंट/स्कर्ट',
    items: [
      'Sky Blue collared shirt (neatly ironed)',
      'Black formal trousers (Boys) / Pleated skirt (Girls)',
      'School tie & belt',
      'Black school shoes & matching socks',
      'School ID card mandatory'
    ],
    tag: 'Academic Day'
  },
  {
    key: 'saturday',
    dayName: 'Saturday',
    dayHindi: 'शनिवार',
    dayIndex: 6,
    type: 'HOUSE DRESS',
    typeHindi: 'हाउस ड्रेस',
    isHouse: true,
    colorTheme: '#c62828',
    bgBadge: 'badge-red',
    description: 'House T-Shirt & Track Pants',
    descriptionHindi: 'हाउस टी-शर्ट और ट्रैक पैंट',
    items: [
      'Assigned House Color T-Shirt',
      'Official school track pants',
      'Sports / PT shoes with white socks',
      'Co-curricular & physical fitness sessions',
      'School ID card mandatory'
    ],
    tag: 'Weekend House & Sports'
  }
];

const INSTRUCTIONS = [
  {
    id: 1,
    title: 'Clean & Ironed',
    titleHindi: 'स्वच्छ एवं इस्त्री',
    icon: Shirt,
    color: '#0c278c',
    text: 'Students must wear clean, neat and properly ironed uniform every day.',
    textHindi: 'विद्यार्थियों को प्रतिदिन स्वच्छ, साफ-सुथरी और अच्छी तरह से इस्त्री की गई यूनिफॉर्म पहननी चाहिए।'
  },
  {
    id: 2,
    title: 'Shoes & Socks',
    titleHindi: 'जूते और मोज़े',
    icon: Footprints,
    color: '#1b5e20',
    text: 'Black school shoes with matching socks are compulsory on regular uniform days.',
    textHindi: 'नियमित यूनिफॉर्म के दिनों में काले स्कूल जूते और मैचिंग मोज़े अनिवार्य हैं।'
  },
  {
    id: 3,
    title: 'School ID Card',
    titleHindi: 'स्कूल पहचान पत्र (ID Card)',
    icon: IdCard,
    color: '#c2185b',
    text: 'The school ID card is mandatory every day for security and identity verification.',
    textHindi: 'सुरक्षा और पहचान के लिए स्कूल का पहचान पत्र (ID Card) हर दिन अनिवार्य है।'
  },
  {
    id: 4,
    title: 'Prescribed Dress Code',
    titleHindi: 'निर्धारित ड्रेस कोड',
    icon: UserCheck,
    color: '#e65100',
    text: 'Students are expected to strictly follow the prescribed dress code on the respective day.',
    textHindi: 'विद्यार्थियों से संबंधित दिन पर निर्धारित ड्रेस कोड का पूर्ण पालन करने की अपेक्षा की जाती है।'
  },
  {
    id: 5,
    title: 'Parental Support',
    titleHindi: 'अभिभावक सहयोग',
    icon: ShieldCheck,
    color: '#5e35b1',
    text: 'Parents are requested to ensure that their child wears the correct uniform before leaving home.',
    textHindi: 'अभिभावकों से अनुरोध है कि वे सुनिश्चित करें कि बच्चा घर से निकलते समय सही यूनिफॉर्म में हो।'
  }
];

const HOUSES = [
  { name: 'Red House', color: '#dc2626', badge: 'Passion & Courage' },
  { name: 'Blue House', color: '#2563eb', badge: 'Loyalty & Truth' },
  { name: 'Green House', color: '#16a34a', badge: 'Growth & Harmony' },
  { name: 'Yellow House', color: '#ca8a04', badge: 'Wisdom & Energy' }
];

export default function UniformSchedule() {
  const { t, i18n } = useTranslation();
  const isHindi = i18n.language === 'hi';

  const [activeTab, setActiveTab] = useState('all'); // 'all', 'regular', 'house'
  const [showPosterModal, setShowPosterModal] = useState(false);

  // Determine current day for the smart badge
  const todayIndex = new Date().getDay(); // 0 = Sun, 1 = Mon ... 6 = Sat
  const todaySchedule = useMemo(() => {
    return DAYS_SCHEDULE.find((item) => item.dayIndex === todayIndex);
  }, [todayIndex]);

  const filteredDays = useMemo(() => {
    if (activeTab === 'regular') {
      return DAYS_SCHEDULE.filter((d) => !d.isHouse);
    }
    if (activeTab === 'house') {
      return DAYS_SCHEDULE.filter((d) => d.isHouse);
    }
    return DAYS_SCHEDULE;
  }, [activeTab]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <StructuredData type="organization" />
      <Helmet>
        <title>School Dress Code & Uniform Schedule - Prabhat Tara Vidya Niketan</title>
        <meta
          name="title"
          content="School Dress Code & Uniform Schedule - Prabhat Tara Vidya Niketan"
        />
        <meta
          name="description"
          content="Official weekly dress code and uniform schedule for Prabhat Tara Vidya Niketan. View day-wise uniform guidelines for Monday through Saturday, house dresses, footwear, and important instructions."
        />
        <meta
          name="keywords"
          content="Prabhat Tara Vidya Niketan uniform, school dress code schedule, PTVN uniform, school dress timetable, sky blue shirt black pant, house dress, Wednesday Saturday uniform, school uniform rules"
        />
        <link rel="canonical" href="https://prabhattaravidyaniketan.in/uniform-schedule" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://prabhattaravidyaniketan.in/uniform-schedule" />
        <meta
          property="og:title"
          content="School Dress Code & Uniform Schedule - Prabhat Tara Vidya Niketan"
        />
        <meta
          property="og:description"
          content="Official weekly dress code and uniform schedule for Prabhat Tara Vidya Niketan Goraul. Sky blue shirt, black pants/skirt, and house dress details."
        />
        <meta property="og:image" content="https://prabhattaravidyaniketan.in/images/dress-code-schedule.jpg" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://prabhattaravidyaniketan.in/uniform-schedule" />
        <meta
          property="twitter:title"
          content="School Dress Code & Uniform Schedule - Prabhat Tara Vidya Niketan"
        />
        <meta
          property="twitter:description"
          content="Official weekly dress code schedule for Prabhat Tara Vidya Niketan."
        />
        <meta property="twitter:image" content="https://prabhattaravidyaniketan.in/images/dress-code-schedule.jpg" />
      </Helmet>

      <div className="uniform-schedule-page">
        {/* Top Breadcrumb */}
        <div className="schedule-breadcrumb">
          <Link to="/">{t('home')}</Link>
          <span>/</span>
          <span className="current">{isHindi ? 'यूनिफॉर्म समय-सारणी' : 'Uniform Schedule'}</span>
        </div>

        {/* Hero Header Section */}
        <section className="schedule-hero">
          <div className="hero-emblem-wrap">
            <img
              src="/images/logo.jpg"
              alt="Prabhat Tara Vidya Niketan Logo"
              className="school-emblem"
            />
          </div>

          <div className="hero-content">
            <span className="school-super-title">
              PRABHAT TARA VIDYA NIKETAN HIGH SCHOOL
            </span>
            <div className="motto-strip">
              <span className="star">★</span>
              <span className="star">★</span>
              <span className="star">★</span>
              <span className="motto-text">
                {isHindi
                  ? 'ड्रेस में अनुशासन, जीवन में अनुशासन दर्शाता है'
                  : 'DISCIPLINE IN DRESS, REFLECTS DISCIPLINE IN LIFE'}
              </span>
              <span className="star">★</span>
              <span className="star">★</span>
              <span className="star">★</span>
            </div>

            <h1 className="main-title">
              {isHindi ? 'स्कूल ड्रेस कोड समय-सारणी' : 'SCHOOL DRESS CODE SCHEDULE'}
            </h1>
            <p className="hero-lead">
              {isHindi
                ? 'एक स्वच्छ और सुव्यवस्थित स्कूल यूनिफॉर्म विद्यार्थियों में समानता, अनुशासन और आत्मविश्वास का संचार करती है। कृपया नीचे दी गई साप्ताहिक समय-सारणी का नियमित पालन करें।'
                : 'A clean, well-ordered uniform nurtures equality, discipline, and school pride. Please adhere to the day-wise dress code guidelines outlined below.'}
            </p>

            <div className="hero-action-buttons no-print">
              <button
                className="btn-action btn-poster"
                onClick={() => setShowPosterModal(true)}
              >
                <Eye size={18} />
                <span>{isHindi ? 'आधिकारिक नोटिस पोस्टर देखें' : 'View Official Poster'}</span>
              </button>
              <a
                href="/images/dress-code-schedule.jpg"
                download="PTVN-Uniform-Schedule.jpg"
                className="btn-action btn-download"
              >
                <Download size={18} />
                <span>{isHindi ? 'पोस्टर डाउनलोड करें' : 'Download Poster'}</span>
              </a>
              <button className="btn-action btn-print" onClick={handlePrint}>
                <Printer size={18} />
                <span>{isHindi ? 'प्रिंट करें' : 'Print Schedule'}</span>
              </button>
            </div>
          </div>
        </section>

        {/* Live "Today's Uniform" Alert Card */}
        <section className="today-uniform-card no-print">
          <div className="today-badge">
            <Clock size={18} />
            <span>{isHindi ? 'आज का ड्रेस कोड' : "TODAY'S DRESS CODE"}</span>
          </div>
          <div className="today-info">
            {todaySchedule ? (
              <>
                <div className="today-day">
                  <strong>{isHindi ? todaySchedule.dayHindi : todaySchedule.dayName}:</strong>{' '}
                  <span
                    className={`uniform-tag ${
                      todaySchedule.isHouse ? 'tag-house' : 'tag-regular'
                    }`}
                  >
                    {isHindi ? todaySchedule.typeHindi : todaySchedule.type}
                  </span>
                </div>
                <div className="today-desc">
                  {isHindi ? todaySchedule.descriptionHindi : todaySchedule.description}
                </div>
              </>
            ) : (
              <>
                <div className="today-day">
                  <strong>{isHindi ? 'रविवार (अवकाश)' : 'Sunday (School Holiday)'}:</strong>{' '}
                  <span className="uniform-tag tag-holiday">
                    {isHindi ? 'अवकाश' : 'Holiday'}
                  </span>
                </div>
                <div className="today-desc">
                  {isHindi
                    ? 'कल सोमवार है: नियमित स्कूल यूनिफॉर्म (आसमानी नीली शर्ट, काली पैंट/स्कर्ट) तैयार रखें।'
                    : 'Tomorrow is Monday: Prepare Regular School Uniform (Sky Blue Shirt, Black Pant/Skirt).'}
                </div>
              </>
            )}
          </div>
        </section>

        {/* Interactive Filter Tabs */}
        <section className="schedule-filter-tabs no-print">
          <button
            className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            <Calendar size={16} />
            <span>{isHindi ? 'सभी दिन (सोम - शनि)' : 'All Days (Mon – Sat)'}</span>
          </button>
          <button
            className={`tab-btn ${activeTab === 'regular' ? 'active' : ''}`}
            onClick={() => setActiveTab('regular')}
          >
            <Shirt size={16} />
            <span>
              {isHindi ? 'नियमित यूनिफॉर्म (4 दिन)' : 'Regular Uniform (Mon, Tue, Thu, Fri)'}
            </span>
          </button>
          <button
            className={`tab-btn ${activeTab === 'house' ? 'active' : ''}`}
            onClick={() => setActiveTab('house')}
          >
            <Sparkles size={16} />
            <span>{isHindi ? 'हाउस ड्रेस (बुध, शनि)' : 'House Dress (Wed, Sat)'}</span>
          </button>
        </section>

        {/* Weekly Day-by-Day Cards */}
        <section className="weekly-schedule-grid">
          {filteredDays.map((day) => {
            const isToday = day.dayIndex === todayIndex;
            return (
              <div
                key={day.key}
                className={`day-card ${day.isHouse ? 'house-card' : 'regular-card'} ${
                  isToday ? 'today-highlight' : ''
                }`}
                style={{ '--accent-color': day.colorTheme }}
              >
                {/* Day Header Bar */}
                <div className="day-header" style={{ backgroundColor: day.colorTheme }}>
                  <div className="day-name-group">
                    <span className="calendar-icon">
                      <Calendar size={20} />
                    </span>
                    <span className="day-title">
                      {isHindi ? day.dayHindi : day.dayName.toUpperCase()}
                    </span>
                  </div>
                  {isToday && (
                    <span className="today-pill">
                      {isHindi ? 'आज' : 'TODAY'}
                    </span>
                  )}
                </div>

                {/* Day Body */}
                <div className="day-body">
                  <div className="dress-type-badge">
                    <span className="type-label">
                      {isHindi ? day.typeHindi : day.type}
                    </span>
                  </div>

                  <div className="dress-description">
                    <p className="desc-text">
                      {isHindi ? day.descriptionHindi : day.description}
                    </p>
                  </div>

                  {/* Visual representation chips */}
                  <div className="visual-chips">
                    {!day.isHouse ? (
                      <>
                        <span className="chip chip-sky">Sky Blue Shirt</span>
                        <span className="chip chip-black">Black Pant / Skirt</span>
                        <span className="chip chip-shoes">Black Shoes</span>
                      </>
                    ) : (
                      <>
                        <span className="chip chip-house-red">House T-Shirt</span>
                        <span className="chip chip-track">Track Pants</span>
                        <span className="chip chip-sports">PT Shoes</span>
                      </>
                    )}
                  </div>

                  {/* Checklist items */}
                  <ul className="day-checklist">
                    {day.items.map((item, idx) => (
                      <li key={idx}>
                        <CheckCircle2 size={16} className="check-icon" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="day-footer-tag">
                    <span>{day.tag}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </section>

        {/* Important Instructions (Matching 5 Bottom Icons of Poster) */}
        <section className="instructions-section">
          <div className="section-title-wrap">
            <div className="title-decor"></div>
            <h2>{isHindi ? 'महत्वपूर्ण निर्देश' : 'IMPORTANT INSTRUCTIONS'}</h2>
            <div className="title-decor"></div>
          </div>
          <p className="section-subtitle">
            {isHindi
              ? 'प्रत्येक विद्यार्थी और अभिभावक से इन दिशानिर्देशों का अक्षरशः पालन करने की अपेक्षा की जाती है'
              : 'Strict adherence to these guidelines is expected from every student and parent'}
          </p>

          <div className="instructions-grid">
            {INSTRUCTIONS.map((inst) => {
              const IconComp = inst.icon;
              return (
                <div
                  key={inst.id}
                  className="instruction-card"
                  style={{ '--card-accent': inst.color }}
                >
                  <div className="icon-wrapper" style={{ borderColor: inst.color }}>
                    <IconComp size={30} style={{ color: inst.color }} />
                  </div>
                  <h3>{isHindi ? inst.titleHindi : inst.title}</h3>
                  <p>{isHindi ? inst.textHindi : inst.text}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Detailed Guidelines by Category */}
        <section className="uniform-details-section">
          <div className="section-title-wrap">
            <div className="title-decor"></div>
            <h2>{isHindi ? 'विस्तृत ड्रेस कोड विवरण' : 'DETAILED DRESS CODE SPECIFICATIONS'}</h2>
            <div className="title-decor"></div>
          </div>

          <div className="details-columns">
            {/* Boys Uniform Column */}
            <div className="detail-box">
              <div className="box-header header-boys">
                <Shirt size={22} />
                <h3>{isHindi ? 'छात्र (Boys Uniform)' : 'Boys Uniform'}</h3>
              </div>
              <ul className="spec-list">
                <li>
                  <strong>Shirt:</strong> Sky Blue half-sleeve (summer) / full-sleeve (winter) with school collar and logo pocket.
                </li>
                <li>
                  <strong>Trousers:</strong> Black tailored trousers (pleatless or single pleat) with standard belt loops.
                </li>
                <li>
                  <strong>Accessories:</strong> Official school tie & standard black belt with school buckle.
                </li>
                <li>
                  <strong>Footwear:</strong> Polished black school shoes with plain navy/black socks.
                </li>
                <li>
                  <strong>Winter Wear:</strong> Navy blue school pullover / blazer with embroidered school crest.
                </li>
              </ul>
            </div>

            {/* Girls Uniform Column */}
            <div className="detail-box">
              <div className="box-header header-girls">
                <Shirt size={22} />
                <h3>{isHindi ? 'छात्राएँ (Girls Uniform)' : 'Girls Uniform'}</h3>
              </div>
              <ul className="spec-list">
                <li>
                  <strong>Shirt / Blouse:</strong> Sky Blue shirt with school collar and emblem.
                </li>
                <li>
                  <strong>Skirt / Trousers:</strong> Black pleated skirt (below knee length) or black formal trousers for higher classes.
                </li>
                <li>
                  <strong>Accessories:</strong> School tie, black belt, and black ribbons / plain black hairband for neatly tied hair.
                </li>
                <li>
                  <strong>Footwear:</strong> Black school buckle/lace shoes with matching socks.
                </li>
                <li>
                  <strong>Winter Wear:</strong> Navy blue school pullover / blazer with embroidered school crest.
                </li>
              </ul>
            </div>

            {/* House System Column */}
            <div className="detail-box">
              <div className="box-header header-house">
                <Sparkles size={22} />
                <h3>{isHindi ? 'हाउस ड्रेस (Wednesday & Saturday)' : 'House Dress System'}</h3>
              </div>
              <p className="house-intro">
                Students wear their assigned house polo/round neck T-Shirt on <strong>Wednesdays and Saturdays</strong> for PT, sports, and co-curricular house meets:
              </p>
              <div className="houses-chip-list">
                {HOUSES.map((h) => (
                  <div key={h.name} className="house-pill" style={{ borderColor: h.color }}>
                    <span className="dot" style={{ backgroundColor: h.color }}></span>
                    <span className="house-name">{h.name}</span>
                    <span className="house-badge">{h.badge}</span>
                  </div>
                ))}
              </div>
              <ul className="spec-list" style={{ marginTop: '1rem' }}>
                <li>
                  <strong>Bottoms:</strong> Official navy/black sports track pants with house color piping.
                </li>
                <li>
                  <strong>Footwear:</strong> White canvas / sports PT shoes with clean white socks.
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* General Grooming & Rules */}
        <section className="grooming-notice">
          <div className="notice-icon">
            <AlertCircle size={28} />
          </div>
          <div className="notice-content">
            <h4>{isHindi ? 'सामान्य अनुशासन एवं ग्रूमिंग नियम' : 'General Grooming & Code of Conduct'}</h4>
            <ul>
              <li>
                {isHindi
                  ? 'बाल साफ, संवरे और शालीन ढंग से कटे होने चाहिए। छात्रों के बाल छोटे होने चाहिए और छात्राओं के लंबे बाल दो चोटियों या पोनीटेल में बंधे होने चाहिए।'
                  : 'Hair must be neatly combed and maintained. Boys should have a standard neat haircut; girls with long hair must braid or tie it neatly with black bands.'}
              </li>
              <li>
                {isHindi
                  ? 'नाखून साफ और कटे होने चाहिए। नेल पॉलिश, टैटू, मेहंदी (विशेष उत्सवों के अतिरिक्त) या कीमती आभूषण पहनना वर्जित है।'
                  : 'Nails must be neatly clipped. Nail polish, tattoos, mehendi (except festive permissions), or expensive jewelry are strictly prohibited.'}
              </li>
              <li>
                {isHindi
                  ? 'स्कूल यूनिफॉर्म में किसी भी प्रकार का अनधिकृत बदलाव या फैशनेबल फेरबदल स्वीकार्य नहीं है।'
                  : 'Low-waist pants, tight fitting, or altered hemlines deviating from school design will not be permitted.'}
              </li>
            </ul>
          </div>
        </section>

        {/* Fee Structure / Cost Link Banner */}
        <section className="uniform-pricing-banner no-print">
          <div className="banner-left">
            <Shirt size={32} />
            <div>
              <h3>{isHindi ? 'यूनिफॉर्म किट और लागत की जानकारी चाहिए?' : 'Need Uniform Sizing & Pricing Details?'}</h3>
              <p>
                {isHindi
                  ? 'यूनिफॉर्म सेट, टाई, बेल्ट एवं अन्य शुल्क विवरण के लिए शुल्क संरचना पृष्ठ देखें।'
                  : 'Check out the complete breakdown of uniform costs, sizes (24-40), tie, belt, and academic fee details.'}
              </p>
            </div>
          </div>
          <div className="banner-right">
            <Link to="/fee-structure" className="btn-fee-link">
              <span>{isHindi ? 'शुल्क संरचना देखें' : 'View Fee Structure'}</span>
              <ArrowRight size={18} />
            </Link>
            <a href="tel:7654164726" className="btn-call-link">
              <Phone size={16} />
              <span>+91 7654164726</span>
            </a>
          </div>
        </section>

        {/* Poster Bottom Slogan */}
        <div className="poster-footer-slogan">
          <div className="stars">
            <span>★</span>
            <span>★</span>
          </div>
          <p className="slogan-text">Smart Uniform, Bright Future</p>
          <div className="stars">
            <span>★</span>
            <span>★</span>
          </div>
        </div>

        {/* Modal for viewing the official poster */}
        {showPosterModal && (
          <div className="poster-modal-overlay" onClick={() => setShowPosterModal(false)}>
            <div className="poster-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{isHindi ? 'आधिकारिक स्कूल ड्रेस कोड नोटिस' : 'Official School Dress Code Notice'}</h3>
                <button
                  className="modal-close-btn"
                  onClick={() => setShowPosterModal(false)}
                  aria-label="Close"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="modal-body">
                <img
                  src="/images/dress-code-schedule.jpg"
                  alt="Prabhat Tara Vidya Niketan Uniform Schedule Official Poster"
                  className="full-poster-image"
                />
              </div>
              <div className="modal-footer">
                <a
                  href="/images/dress-code-schedule.jpg"
                  download="PTVN-Uniform-Schedule.jpg"
                  className="btn-download-modal"
                >
                  <Download size={18} />
                  <span>{isHindi ? 'पोस्टर डाउनलोड करें' : 'Download High-Res Poster'}</span>
                </a>
                <button
                  className="btn-close-modal"
                  onClick={() => setShowPosterModal(false)}
                >
                  {isHindi ? 'बंद करें' : 'Close'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

